// Custom DnD sensor preventing drag on interactive elements.
import { PointerSensor } from '@dnd-kit/core';

/**
 * A custom dnd-kit sensor that extends `PointerSensor` to prevent drag
 * operations from starting on interactive elements.
 *
 * ## Why this exists
 * dnd-kit's default `PointerSensor` fires on any `pointerdown` event,
 * including clicks on buttons, inputs, and other interactive controls inside
 * a draggable item. This sensor intercepts those events and blocks the drag
 * when the pointer is over an interactive element.
 *
 * ## Activation conditions (ALL must be true to start a drag)
 * 1. `event.isPrimary !== false` — ignores secondary touch points on multi-touch
 *    (using `!== false` instead of `=== true` to also accept `undefined` from
 *     test automation like Playwright, which synthesizes pointer events without
 *     the `isPrimary` property)
 * 2. `event.button === 0`       — left mouse button only (not right-click or middle-click)
 * 3. `!isInteractiveElement(event.target)` — the target is not a blocking element
 *
 * ## The `[data-no-dnd]` convention
 * Any element that is not in the default interactive-element list can opt out
 * of dragging by adding the `data-no-dnd` attribute:
 * ```jsx
 * <div data-no-dnd>Click me without dragging the parent</div>
 * ```
 * This is the preferred way to block drag on custom interactive controls
 * (e.g. icon buttons without a `<button>` wrapper).
 */
export class CustomPointerSensor extends PointerSensor {
  /** @type {any} */
  static activators = [
    {
      eventName: 'onPointerDown',
      // dnd-kit sensors receive the native PointerEvent directly,
      // not a React SyntheticEvent — so `event` here is the DOM event.
      handler: (event) => {
        const nativeEvent = 'nativeEvent' in event ? event.nativeEvent : event;
        if (
          nativeEvent.isPrimary === false ||
          nativeEvent.button !== 0 ||
          isInteractiveElement(nativeEvent.target)
        ) {
          return false;
        }

        return true;
      },
    },
  ];
}

/**
 * Determines whether a DOM element (or any of its ancestors) should block
 * the start of a drag operation.
 *
 * Uses `element.closest()` to walk the full ancestor chain, so clicking a
 * `<span>` inside a `<button>` is correctly identified as interactive.
 *
 * ### Blocked elements
 * - `button`, `input`, `textarea`, `select`, `option`
 * - Any element with the `[data-no-dnd]` attribute (escape-hatch for custom controls)
 *
 * @param {EventTarget|null|undefined} target - The event target from a pointer event.
 * @returns {boolean} `true` if the drag should be blocked; `false` if it may proceed.
 */
/** @public */
export function isInteractiveElement(target) {
  if (!(target instanceof Element)) {
    return false;
  }
  const interactiveElements = [
    'button',
    'input',
    'textarea',
    'select',
    'option',
    // We rely on specific buttons or elements having '[data-no-dnd]'
    // to prevent dragging, which is more explicit.
    '[data-no-dnd]',
  ];

  if (target.closest(interactiveElements.join(','))) {
    return true;
  }

  return false;
}
